import { motion } from "framer-motion";
import { Compass, Cpu, Layers, Heart, Sparkles, Rocket, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import Engine from "@/components/sections/Engine";

const values = [
  { icon: Compass, title: "Systems over campaigns", desc: "We engineer durable growth machinery, not one-off pushes.", color: "from-neon-orange to-neon-magenta" },
  { icon: Cpu, title: "AI as infrastructure", desc: "Agents, copilots and automations baked into every layer.", color: "from-neon-cyan to-neon-blue" },
  { icon: Layers, title: "Compound by design", desc: "Each layer strengthens the next — brand → demand → conversion.", color: "from-neon-magenta to-neon-purple" },
  { icon: Heart, title: "Client outcomes, not deliverables", desc: "Revenue moved, hours saved, retention improved.", color: "from-emerald-400 to-neon-cyan" },
];

const timeline = [
  { year: "2022", title: "Founded", desc: "Started as a growth practice for ambitious founders." },
  { year: "2023", title: "Brand × Growth", desc: "Added a creative arm — brand systems integrated with performance." },
  { year: "2024", title: "Automation Lab", desc: "Launched the in-house AI Lab. First proprietary internal tools." },
  { year: "2025", title: "Scale Operations", desc: "150+ projects, ₹100Cr+ revenue influenced, 96% retention." },
  { year: "2026", title: "Productized Growth", desc: "Roadmap of vertical AI products — Real Estate, Education, Healthcare." },
];

const team = [
  { name: "Aarav Mehta", role: "Founder & Growth Lead", color: "from-neon-orange to-neon-magenta", initials: "AM" },
  { name: "Priya Shah", role: "Head of Brand", color: "from-neon-magenta to-neon-purple", initials: "PS" },
  { name: "Rohan Verma", role: "Head of AI Lab", color: "from-neon-cyan to-neon-blue", initials: "RV" },
  { name: "Neha Iyer", role: "Director, Performance", color: "from-neon-purple to-neon-blue", initials: "NI" },
];

export default function About() {
  return (
    <div data-testid="about-root">
      <PageHero
        eyebrow="About Novarise"
        title={<>We don&apos;t run campaigns. <span className="text-gradient-neon">We build growth machines.</span></>}
        subtitle="Novarise Works is a growth infrastructure company. We help ambitious businesses turn attention into compounding revenue through brand, demand, conversion and AI-powered automation — engineered as one connected system."
      />

      {/* Who we are + image */}
      <section className="section pt-0">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-4">Who we are</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              A studio of founders, strategists, designers, marketers and AI engineers — operating as one growth team.
            </h2>
            <p className="mt-5 text-white/65 leading-relaxed max-w-2xl">
              The growth stack today is fractured. Agencies sell deliverables.
              Tools sell features. Founders are left stitching it together.
              Novarise exists to build that missing layer — the operating system
              that runs underneath your brand, your funnels and your team.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
              {[{n: "150+", l: "Projects"}, {n: "₹100Cr+", l: "Revenue influenced"}, {n: "96%", l: "Retention"}].map((s) => (
                <div key={s.l} className="glass rounded-xl p-4">
                  <div className="font-mono text-2xl font-extrabold text-gradient-neon">{s.n}</div>
                  <div className="text-xs text-white/55 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative rounded-3xl overflow-hidden border border-white/[0.06]"
            >
              <img src="/images/hero.png" alt="Novarise team" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-bg-0 via-bg-0/30 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4">
                <p className="overline mb-1">Mission</p>
                <p className="text-sm text-white/85">Engineer growth infrastructure for the next 10,000 ambitious businesses.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="overline mb-4">How we think</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Four operating principles.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="glass rounded-2xl p-6 relative overflow-hidden"
                >
                  <div className={`absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-25 bg-gradient-to-tr ${v.color}`} />
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${v.color} flex items-center justify-center`}>
                    <Icon size={18} />
                  </div>
                  <h3 className="mt-5 text-lg font-bold">{v.title}</h3>
                  <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process / Engine */}
      <Engine />

      {/* Timeline */}
      <section className="section relative">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <p className="overline mb-4 flex items-center gap-2"><Sparkles size={12} /> Timeline</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">A short, intentional history.</h2>
          </div>
          <div className="relative">
            <div className="absolute left-3 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-neon-cyan/30 via-neon-magenta/30 to-transparent" />
            <ul className="space-y-10">
              {timeline.map((t, i) => (
                <motion.li
                  key={t.year}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.55 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-12 items-start"
                >
                  <div className={`pl-10 sm:pl-0 ${i % 2 === 0 ? "sm:text-right" : "sm:col-start-2"}`}>
                    <div className="font-mono text-sm text-white/45">{t.year}</div>
                    <h3 className="mt-1 text-2xl font-bold tracking-tight">{t.title}</h3>
                    <p className="mt-2 text-white/60 max-w-md sm:ml-auto">{t.desc}</p>
                  </div>
                  <div className="hidden sm:block" />
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section relative">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <p className="overline mb-4 flex items-center gap-2"><Users size={12} /> Team</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">The operators behind the systems.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {team.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="glass rounded-2xl p-5 text-center"
              >
                <div className={`mx-auto w-20 h-20 rounded-full bg-gradient-to-br ${p.color} flex items-center justify-center font-mono font-extrabold text-2xl`}>
                  {p.initials}
                </div>
                <div className="mt-4 font-semibold">{p.name}</div>
                <div className="text-xs text-white/55 mt-1">{p.role}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        headline={<>Want to build with us? <span className="text-gradient-neon">Start the conversation.</span></>}
        sub="We take on a small number of partners each quarter. Send a few lines about your business."
        primary={{ label: "Book Strategy Call", to: "/contact" }}
        secondary={{ label: "See Our Work", to: "/case-studies" }}
      />
    </div>
  );
}
